import { useEffect, useRef, useState } from 'react';

// Generic hook for a single controlled text value that commits to the store
// after a delay -- mirrors useDebouncedRange for non-numeric single-value
// fields (e.g. zipcode, city).
export function useDebouncedValue(
  storeValue: string | undefined,
  onCommit: (value: string | undefined) => void,
  delay = 400
) {
  const [ local, setLocal ] = useState<string>(storeValue ?? '');

  const isLocalUpdate = useRef(false);

  // Keep the latest onCommit/delay available to the timer below without
  // putting them in its dependency array -- onCommit is a fresh closure on
  // every render of the caller (not memoized), so depending on it directly
  // would reset this field's debounce timer whenever a SIBLING field's
  // keystroke re-renders the parent and recreates both closures.
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const delayRef = useRef(delay);
  delayRef.current = delay;

  // Commit local -> store (debounced)
  useEffect(() => {
    isLocalUpdate.current = true;

    const timer = setTimeout(() => {
      const trimmed = local.trim();
      onCommitRef.current(trimmed || undefined);
      // allow store-to-local sync again
      setTimeout(() => (isLocalUpdate.current = false), 0);
    }, delayRef.current);

    return () => clearTimeout(timer);
  }, [ local ]);

  // Store -> local sync (reset, preset changes)
  useEffect(() => {
    if (!isLocalUpdate.current) {
      setLocal(storeValue ?? '');
    }
  }, [ storeValue ]);

  return {
    local,
    setLocal,
  };
}
